import { useState, useLayoutEffect, useCallback } from "react";

/**
 * This hooks lets you switch between a Controlled vs. Uncontrolled hook. If a setter/getter pair from a `useState` is passed into externalValue and setExternal value, the hook will act as if the state is being controlled by the passed in getter/setter
 * This hook lets a component create a state/setter or pass through state/setter from props. externalValue and setExternalValue are the `state` and `setState` that are passed from the ancestor respectively
 *
 * ex.
 * const Foo = ({ ancestorState, setAncestorState }) => {
 *   const [state, setState] = useControlledState({defaultValue: null,  externalValue: ancestorState, setExternalValue: setAncestorState })
 *   // `state` will be the value passed in from the ancestor if `setAncestorState` exists, otherwise `useControlledState` will create it's own state
 * }
 *
 * const Bar = () => {
 *  const [state, setState] = useState(0);
 *  return <Foo ancestorState={state} setAncestorState={setState} />
 * }
 **/
export function useControlledState<T = any>({
  defaultValue,
  externalValue,
  setExternalValue,
}: {
  defaultValue: T;
  externalValue: T;
  setExternalValue: React.Dispatch<React.SetStateAction<T>>;
}): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [internalValue, setInternalValue] = useState<T>(defaultValue);
  const setBoth = useCallback(
    (val: T | ((val: T) => T)) => {
      setInternalValue(val);
      setExternalValue(val);
    },
    [setInternalValue, setExternalValue],
  );

  // control the `inner` value if the optional value changes
  useLayoutEffect(() => {
    // if setExternalValue exists, we are assuming that we want to pass through state from the ancestor
    if (!setExternalValue) return;

    if (externalValue !== internalValue) {
      setInternalValue(externalValue);
    }
  }, [externalValue, setExternalValue, setInternalValue]);

  // if setExternalValue exists, we are assumming that we want to pass through state from the ancestor
  if (setExternalValue) {
    return [externalValue, setBoth];
  }

  return [internalValue, setInternalValue];
}
